import React, { useState, useEffect, useRef } from "react";
import { Field } from "react-final-form";
import { Box, TextField, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import InputMask from "react-input-mask";

interface HourMinuteInputProps {
  source: string;
  label?: string;
  /**
   * Maximum number of hour digits (1-8)
   * @default 4
   */
  hourDigits?: number;
  disabled?: boolean;
  validate?: ((value: any, allValues?: any) => any) | ((value: any, allValues?: any) => any)[];
  fullWidth?: boolean;
  variant?: "standard" | "filled" | "outlined";
  defaultValue?: any;
}

interface HourMinuteFieldProps {
  input: any;
  meta: any;
  label?: string;
  hourDigits?: number;
  disabled?: boolean;
}

const useStyles = makeStyles(theme => ({
  container: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    position: "relative",
    gap: theme.spacing(1),
    // Align with other TextField components by accounting for the label space
    // Material-UI TextField with label has extra top spacing, we match it here
    paddingTop: "1px",
    marginTop: "9px",
  },
  inputWrapper: {
    flex: 1,
    minWidth: 0,
  },
  separator: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    fontSize: "1rem",
    fontWeight: "bold",
    color: theme.palette.text.secondary,
    pointerEvents: "none",
    zIndex: 1,
    backgroundColor: "white",
    padding: "0 4px",
  },
}));

/**
 * Internal component that handles the hour/minute input logic
 */
const HourMinuteField: React.FC<HourMinuteFieldProps> = ({ input, meta, label, hourDigits = 4, disabled }) => {
  const classes = useStyles();

  const validatedHourDigits = Math.max(1, Math.min(8, hourDigits));
  const hourMask = "9".repeat(validatedHourDigits);
  const hourPlaceholder = "H".repeat(validatedHourDigits);

  const [hours, setHours] = useState<string>("");
  const [minutes, setMinutes] = useState<string>("");
  const isInternalUpdate = useRef(false);

  // Convert total minutes from backend to hours and minutes for display
  useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    const value = input.value;

    if (value === null || value === undefined || value === "") {
      setHours("");
      setMinutes("");
      return;
    }

    const totalMinutes = Number(value);
    if (isNaN(totalMinutes)) {
      setHours("");
      setMinutes("");
      return;
    }

    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;

    // Saat kısmında gereksiz sıfırları kaldır (0 ise sadece "0", değilse sayıyı olduğu gibi göster)
    const newHours = h === 0 ? "0" : String(h);
    const newMinutes = String(m).padStart(2, "0");

    setHours(newHours);
    setMinutes(newMinutes);
  }, [input.value, validatedHourDigits]);

  // Update backend value when hours or minutes change
  const updateBackendValue = (newHours: string, newMinutes: string) => {
    const cleanedHours = newHours.replace(/_/g, "").replace(/H/g, "");
    const cleanedMinutes = newMinutes.replace(/_/g, "").replace(/M/g, "");

    // If both fields are empty, send null to backend
    if (cleanedHours === "" && cleanedMinutes === "") {
      if (input.value !== null) {
        isInternalUpdate.current = true;
        input.onChange(null);
        input.onBlur();
      }
      return;
    }

    const h = parseInt(cleanedHours) || 0;
    const m = Math.min(parseInt(cleanedMinutes) || 0, 59);
    const totalMinutes = h * 60 + m;

    if (input.value !== totalMinutes) {
      isInternalUpdate.current = true;
      input.onChange(totalMinutes);
      input.onBlur();
    }
  };

  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setHours(value);
    updateBackendValue(value, minutes);
  };

  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    const cleanedMinutes = value.replace(/_/g, "").replace(/M/g, "");

    // Allow empty value to clear the field
    if (cleanedMinutes === "") {
      setMinutes(value);
      updateBackendValue(hours, value);
      return;
    }

    const minuteValue = parseInt(cleanedMinutes) || 0;
    if (minuteValue > 59) {
      return;
    }

    setMinutes(value);
    updateBackendValue(hours, value);
  };

  const handleHoursFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      e.target.setSelectionRange(0, 0);
    }, 0);
  };

  const handleMinutesFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setTimeout(() => {
      const length = e.target.value.length;
      e.target.setSelectionRange(length, length);
    }, 0);
  };

  const handleHoursClick = (e: React.MouseEvent<HTMLInputElement>) => {
    setTimeout(() => {
      e.currentTarget.setSelectionRange(0, 0);
    }, 0);
  };

  const handleMinutesClick = (e: React.MouseEvent<HTMLInputElement>) => {
    setTimeout(() => {
      const length = e.currentTarget.value.length;
      e.currentTarget.setSelectionRange(length, length);
    }, 0);
  };

  const error = meta.touched && meta.error;
  const invalid = meta.touched && meta.invalid;

  return (
    <Box className={classes.container}>
      <Box className={classes.inputWrapper}>
        <InputMask
          mask={hourMask}
          maskChar=""
          value={hours}
          onChange={handleHoursChange}
          onFocus={handleHoursFocus}
          onClick={handleHoursClick}
          disabled={disabled}
        >
          {(inputProps: any) => (
            <TextField
              {...inputProps}
              label={label ? `${label}` : "Hour"}
              variant="outlined"
              size="small"
              fullWidth
              placeholder={hourPlaceholder}
              error={invalid}
              disabled={disabled}
            />
          )}
        </InputMask>
      </Box>

      <Typography className={classes.separator}>:</Typography>

      <Box className={classes.inputWrapper}>
        <InputMask
          mask="99"
          maskChar=""
          value={minutes}
          onChange={handleMinutesChange}
          onFocus={handleMinutesFocus}
          onClick={handleMinutesClick}
          disabled={disabled}
        >
          {(inputProps: any) => (
            <TextField
              {...inputProps}
              label="Minute"
              variant="outlined"
              size="small"
              fullWidth
              placeholder="MM"
              error={invalid}
              helperText={error}
              disabled={disabled}
            />
          )}
        </InputMask>
      </Box>
    </Box>
  );
};

/**
 * Custom input component for entering hours and minutes as separate inputs
 * Converts the input to total minutes for backend storage (e.g., 01:30 -> 90 minutes)
 * Displays separate hour and minute inputs for user-friendly input
 *
 * Backend Storage: Total minutes = (hours * 60) + minutes
 * UI Display: Two separate inputs for hours and minutes
 *
 * @param hourDigits - Number of hour digits (min: 1, max: 8, default: 4)
 *
 * Examples:
 * - Input: Hours: 0123, Minutes: 45 -> Backend: 7425 minutes (123*60 + 45)
 * - Backend: 7425 minutes -> Display: Hours: 0123, Minutes: 45
 * - Minutes are always validated to be <= 59
 */
export const HourMinuteInput: React.FC<HourMinuteInputProps> = props => {
  const { source, label, hourDigits = 4, disabled, validate, ...rest } = props;

  // Compose validators if it's an array
  const composedValidate = React.useMemo(() => {
    if (!validate) return undefined;

    if (Array.isArray(validate)) {
      return (value: any, allValues?: any) => {
        for (const validator of validate) {
          const error = validator(value, allValues);
          if (error) return error;
        }
        return undefined;
      };
    }

    return validate;
  }, [validate]);

  return (
    <Field name={source} validate={composedValidate}>
      {({ input, meta }) => (
        <HourMinuteField input={input} meta={meta} label={label} hourDigits={hourDigits} disabled={disabled} />
      )}
    </Field>
  );
};

export default HourMinuteInput;
